const parseList = (value?: string) =>
  String(value || '')
    .split(',')
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);

const normalizeUrl = (value?: string) => {
  if (!value) return '';
  return String(value).trim().replace(/\/+$/, '');
};

const normalizeHostname = (value?: string) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .replace(/:\d+$/, '');
};

const getDevProxyPort = () =>
  String(process.env.REACT_APP_DEV_PROXY_PORT || process.env.REACT_APP_PROXY_PORT || '').trim();

const getLocation = () => {
  if (typeof window === 'undefined') return null;
  return window.location;
};

const isLocalhostHost = (host: string) =>
  host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');

const getLoginSubdomain = () =>
  (process.env.REACT_APP_LOGIN_SUBDOMAIN || 'login').toLowerCase();

const getAdminSubdomain = () =>
  (process.env.REACT_APP_ADMIN_SUBDOMAIN || 'admin').toLowerCase();

const getApiSubdomain = () =>
  (process.env.REACT_APP_API_SUBDOMAIN || 'api').toLowerCase();

const traceRuntimeEnabled = () =>
  String(process.env.REACT_APP_AUTH_TRACE || process.env.REACT_APP_RUNTIME_TRACE || '')
    .toLowerCase() === 'true';

const traceRuntime = (label: string, payload: Record<string, unknown>) => {
  if (!traceRuntimeEnabled()) return;
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line no-console
  console.info(`🔎 [RuntimeUrls] ${label}`, payload);
};

export const isLoginHostForClient = () => {
  const loc = getLocation();
  if (!loc) return false;
  const hostname = normalizeHostname(loc.hostname);
  const loginSubdomain = getLoginSubdomain();
  return hostname === loginSubdomain || hostname.startsWith(`${loginSubdomain}.`);
};

export const isLocalEntryHostForClient = () => {
  const loc = getLocation();
  if (!loc) return false;
  const hostname = normalizeHostname(loc.hostname);
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const resolveBaseDomain = (hostname: string) => {
  const envBases = parseList(process.env.REACT_APP_PLATFORM_BASE_DOMAINS)
    .concat(parseList(process.env.REACT_APP_PLATFORM_BASE_DOMAIN))
    .concat(parseList(process.env.PLATFORM_BASE_DOMAINS))
    .concat(parseList(process.env.PLATFORM_BASE_DOMAIN));
  const candidates = Array.from(new Set(envBases)).sort((a, b) => b.length - a.length);
  for (const base of candidates) {
    if (hostname === base) return base;
    if (hostname.endsWith(`.${base}`)) return base;
  }
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return 'localhost';
  if (hostname === '127.0.0.1') return '127.0.0.1';
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length >= 2) return parts.slice(-2).join('.');
  return hostname;
};

const buildPortSuffix = (port?: string) => (port ? `:${port}` : '');

const getPortSuffixForHost = (hostname: string, locationPort?: string) => {
  const devProxyPort = getDevProxyPort();
  if (devProxyPort && isLocalhostHost(hostname)) {
    return buildPortSuffix(devProxyPort);
  }
  return buildPortSuffix(locationPort);
};

const getLocalApiPort = (locationPort?: string) => {
  const devProxyPort = getDevProxyPort();
  if (devProxyPort) return devProxyPort;

  const explicitApiPort = String(
    process.env.REACT_APP_LOCAL_API_PORT ||
      process.env.REACT_APP_API_PORT ||
      process.env.REACT_APP_BACKEND_PORT ||
      ''
  ).trim();
  if (explicitApiPort) return explicitApiPort;

  // Safe local default for non-production localhost runs.
  if (String(process.env.NODE_ENV || '').toLowerCase() !== 'production') return '3001';
  return String(locationPort || '').trim();
};

const getApiPortSuffixForHost = (hostname: string, locationPort?: string) => {
  if (isLocalhostHost(hostname)) {
    return buildPortSuffix(getLocalApiPort(locationPort));
  }
  return buildPortSuffix(locationPort);
};

const envOverride = (...values: Array<string | undefined>) => {
  for (const v of values) {
    const cleaned = normalizeUrl(v);
    if (cleaned) return cleaned;
  }
  return '';
};

export const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const runtime = (window as any).__RUNTIME_CONFIG__ as Record<string, unknown> | undefined;
    const runtimeBase = normalizeUrl(
      String(runtime?.API_BASE_URL || runtime?.apiBaseUrl || runtime?.BASE_URL || '')
    );
    if (runtimeBase) {
      return runtimeBase.endsWith('/api') ? runtimeBase : `${runtimeBase}/api`;
    }
  }
  const override = envOverride(process.env.REACT_APP_API_BASE_URL, process.env.REACT_APP_API_URL);
  if (override) {
    return override.endsWith('/api') ? override : `${override}/api`;
  }

  const loc = getLocation();
  if (!loc) return '/api';

  const hostname = normalizeHostname(loc.hostname);
  const protocol = loc.protocol || 'http:';
  const portSuffix = getApiPortSuffixForHost(hostname, loc.port);
  const loginSubdomain = getLoginSubdomain();
  const adminSubdomain = getAdminSubdomain();
  const apiSubdomain = getApiSubdomain();

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const base = resolveBaseDomain(hostname);
    const baseUrl = `${protocol}//${apiSubdomain}.${base}${portSuffix}/api`;
    traceRuntime('apiBase(localhost)', { hostname, baseUrl });
    return baseUrl;
  }

  if (hostname === loginSubdomain || hostname.startsWith(`${loginSubdomain}.`)) {
    const hostWithoutLogin = hostname.startsWith(`${loginSubdomain}.`)
      ? hostname.slice(loginSubdomain.length + 1)
      : hostname;
    const base = resolveBaseDomain(hostWithoutLogin);
    const baseUrl = `${protocol}//${apiSubdomain}.${base}${portSuffix}/api`;
    traceRuntime('apiBase(login)', { hostname, base, baseUrl });
    return baseUrl;
  }

  if (hostname === adminSubdomain || hostname.startsWith(`${adminSubdomain}.`)) {
    const base = resolveBaseDomain(hostname);
    const baseUrl = `${protocol}//${apiSubdomain}.${base}${portSuffix}/api`;
    traceRuntime('apiBase(admin)', { hostname, base, baseUrl });
    return baseUrl;
  }

  if (hostname.startsWith(`${apiSubdomain}.`)) {
    const baseUrl = `${protocol}//${hostname}${portSuffix}/api`;
    traceRuntime('apiBase(apiHost)', { hostname, baseUrl });
    return baseUrl;
  }

  const base = resolveBaseDomain(hostname);
  const baseUrl = `${protocol}//${apiSubdomain}.${base}${portSuffix}/api`;
  traceRuntime('apiBase(tenant)', { hostname, base, baseUrl });
  return baseUrl;
};

export const getSuperAdminApiUrl = () => {
  const override = envOverride(process.env.REACT_APP_SUPERADMIN_API);
  if (override) return override;
  const base = normalizeUrl(getApiBaseUrl());
  return `${base}/superadmin`;
};

export const getControlApiBaseUrl = () => {
  const override = envOverride(process.env.REACT_APP_CONTROL_API_URL);
  if (override) return override;
  const base = normalizeUrl(getApiBaseUrl());
  return base;
};

export const getWsBaseUrl = () => {
  const override = envOverride(process.env.REACT_APP_WS_URL, process.env.NEXT_PUBLIC_WS_URL);
  if (override) return override;
  const base = normalizeUrl(getApiBaseUrl());
  const host = base.replace(/^https?:\/\//, '').replace(/\/api\/?$/, '');
  const protocol = base.startsWith('https') ? 'wss' : 'ws';
  return `${protocol}://${host}`;
};

export const getAdminAppUrl = () => {
  const override = envOverride(process.env.REACT_APP_ADMIN_APP_URL);
  if (override) return override;
  const loc = getLocation();
  if (!loc) return '';
  const hostname = normalizeHostname(loc.hostname);
  const base = resolveBaseDomain(hostname);
  const portSuffix = getPortSuffixForHost(hostname, loc.port);
  const adminSubdomain = getAdminSubdomain();
  if (hostname === adminSubdomain || hostname.startsWith(`${adminSubdomain}.`)) {
    const url = `${loc.protocol}//${hostname}${portSuffix}`;
    traceRuntime('adminUrl(self)', { hostname, url });
    return url;
  }
  const url = `${loc.protocol}//${adminSubdomain}.${base}${portSuffix}`;
  traceRuntime('adminUrl(base)', { hostname, base, url });
  return url;
};

export const getLoginAppUrl = () => {
  const override = envOverride(process.env.REACT_APP_LOGIN_APP_URL);
  if (override) return override;
  const loc = getLocation();
  if (!loc) return '';
  const hostname = normalizeHostname(loc.hostname);
  const base = resolveBaseDomain(hostname);
  const loginSubdomain = getLoginSubdomain();
  const portSuffix = getPortSuffixForHost(hostname, loc.port);

  if (hostname === loginSubdomain || hostname.startsWith(`${loginSubdomain}.`)) {
    const url = `${loc.protocol}//${hostname}${portSuffix}`;
    traceRuntime('loginUrl(self)', { hostname, url });
    return url;
  }

  const url = `${loc.protocol}//${loginSubdomain}.${base}${portSuffix}`;
  traceRuntime('loginUrl(base)', { hostname, base, url });
  return url;
};

export const getTenantAppUrl = (slug?: string) => {
  const override = envOverride(process.env.REACT_APP_TENANT_APP_URL);
  if (override) return override;
  const loc = getLocation();
  if (!loc) return '';
  const hostname = normalizeHostname(loc.hostname);
  const base = resolveBaseDomain(hostname);
  const portSuffix = getPortSuffixForHost(hostname, loc.port);
  const loginSubdomain = getLoginSubdomain();
  const adminSubdomain = getAdminSubdomain();
  const apiSubdomain = getApiSubdomain();

  if (slug) {
    return `${loc.protocol}//${slug}.${base}${portSuffix}`;
  }

  if (
    hostname === adminSubdomain ||
    hostname.startsWith(`${adminSubdomain}.`) ||
    hostname === loginSubdomain ||
    hostname.startsWith(`${loginSubdomain}.`) ||
    hostname.startsWith(`${apiSubdomain}.`)
  ) {
    return `${loc.protocol}//${base}${portSuffix}`;
  }

  return `${loc.protocol}//${hostname}${portSuffix}`;
};

export const resolveBaseDomainForHost = (host: string) => resolveBaseDomain(normalizeHostname(host));

export const getDevProxyPortForClient = () => getDevProxyPort();
